import React, { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import api from 'src/shared/api/axios';
import { listProjectCompanies } from 'src/features/projects/companiesApi';
import { listProjectOrgs, type ProjectOrg } from 'src/features/projects/orgsApi';

// ✅ WbsList와 동일한 트리 키: id/parentId
type Row = {
    id: string; // UI 트리/접기 키
    parentId: string | null; // UI 트리 부모 키
    nodeId: string; // DB 저장 키

    code?: string | null;
    name?: string | null;

    // 현재 화면에서 내려온 “현재 담당회사(Effective)”
    executerId?: string | null; // projectCompanyId
    executer?: string | null; // company name (있으면 사용)

    responsibleType?: 'COMPANY' | 'ORG' | null;
    responsibleId?: string | null;
    responsibleName?: string | null;
};

type Props = {
    projectId: string;
    // wbsListId: string;
    rows: Row[];
    onClose: () => void;
    onPublished: () => void; // publish 후 상위 refresh 트리거
};

type RowEdit = {
    rowId: string; // row.id
    // companyId: string | null; // projectCompanyId
    responsibleType?: 'COMPANY' | 'ORG' | null;
    responsibleId?: string | null;
};

type ResponsibleType = 'COMPANY' | 'ORG';
type ResponsibleRef = { type: ResponsibleType | null; id: string | null };

const encodeRef = (ref: ResponsibleRef) => {
    if (!ref.type || !ref.id) return '';
    return `${ref.type}:${ref.id}`;
};

const decodeRef = (v: string): ResponsibleRef => {
    if (!v) return { type: null, id: null };
    const [type, id] = v.split(':');
    if ((type !== 'COMPANY' && type !== 'ORG') || !id) return { type: null, id: null };
    return { type: type as ResponsibleType, id };
};

const sameRef = (a: ResponsibleRef, b: ResponsibleRef) => a.type === b.type && a.id === b.id;

function sortRows(rs: Row[]) {
    return [...rs].sort((a, b) => {
        const ac = (a.code ?? '').toString();
        const bc = (b.code ?? '').toString();
        if (ac && bc) return ac.localeCompare(bc);
        return (a.name ?? '').toString().localeCompare((b.name ?? '').toString());
    });
}

function buildChildrenMap(rows: Row[]) {
    const m = new Map<string | null, Row[]>();
    for (const r of rows) {
        const key = r.parentId ?? null;
        const arr = m.get(key) ?? [];
        arr.push(r);
        m.set(key, arr);
    }
    for (const [k, v] of m.entries()) m.set(k, sortRows(v));
    return m;
}

function ellipsis(s: string, max = 90) {
    if (!s) return s;
    return s.length > max ? `${s.slice(0, max - 1)}…` : s;
}

export default function AssignResponsibleCompanyModal({ projectId, rows, onClose, onPublished }: Props) {
    const qc = useQueryClient();

    const [collapsed, setCollapsed] = useState<Record<string, boolean>>({}); // key=row.id
    const [local, setLocal] = useState<Record<string, RowEdit>>({}); // key=row.id
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState<string | null>(null);
    const [confirmOpen, setConfirmOpen] = useState(false);

    const childrenMap = useMemo(() => buildChildrenMap(rows), [rows]);
    const roots = useMemo(() => sortRows(rows.filter((r) => r.parentId == null)), [rows]);

    const rowById = useMemo(() => new Map(rows.map((r) => [r.id, r] as const)), [rows]);
    const rowIdByNodeId = useMemo(() => new Map(rows.map((r) => [r.nodeId, r.id] as const)), [rows]);

    const hasChildren = (rowId: string) => (childrenMap.get(rowId) ?? []).length > 0;

    const toggle = (rowId: string) => setCollapsed((p) => ({ ...p, [rowId]: !p[rowId] }));

    const collapseAll = () => {
        const next: Record<string, boolean> = {};
        for (const r of rows) if (hasChildren(r.id)) next[r.id] = true;
        setCollapsed(next);
    };

    const expandAll = () => setCollapsed({});

    function collectDescendants(rowId: string) {
        const out: Row[] = [];
        const stack = [...(childrenMap.get(rowId) ?? [])];
        while (stack.length) {
            const cur = stack.pop()!;
            out.push(cur);
            const kids = childrenMap.get(cur.id) ?? [];
            for (const k of kids) stack.push(k);
        }
        return out;
    }

    // 회사 목록
    const { data: companies = [] } = useQuery({
        queryKey: ['projectCompanies', projectId],
        queryFn: () => listProjectCompanies(projectId),
    });

    function companyNameOf(projectCompanyId: string | null | undefined) {
        if (!projectCompanyId) return '(미지정)';
        const pc = (companies as any[]).find((x) => x.id === projectCompanyId);
        return pc?.company?.name ?? '(unknown)';
    }

    const orgsQueryKey = useMemo(() => ['projectOrgs', projectId] as const, [projectId]);

    const { data: orgs = [] } = useQuery({
        queryKey: orgsQueryKey,
        enabled: !!projectId,
        queryFn: () => listProjectOrgs(projectId),
    });

    function orgNameOf(orgId: string | null | undefined) {
        if (!orgId) return '';
        const o = (orgs as any[]).find((x) => x.id === orgId);
        return (o?.name ?? '').toString();
    }

    function nameOfRef(ref: ResponsibleRef) {
        if (!ref.type || !ref.id) return '';
        return ref.type === 'COMPANY' ? companyNameOf(ref.id) : orgNameOf(ref.id);
    }

    // ✅ Original(기준값) 고정: 모달 오픈 시 rows 기준
    // const originalByRowId = useMemo(() => {
    //     const m = new Map<string, { companyId: string | null; companyName: string }>();
    //     for (const r of rows) {
    //         const cid = r.executerId ?? null;
    //         const cname = (r.executer ?? '').trim() || companyNameOf(cid);
    //         m.set(r.id, { companyId: cid, companyName: cname });
    //     }
    //     // eslint-disable-next-line react-hooks/exhaustive-deps
    //     return m;
    // }, [rows]);
    const originalByRowId = useMemo(() => {
        const m = new Map<string, { ref: ResponsibleRef; name: string }>();
        for (const r of rows) {
            const ref: ResponsibleRef =
                r.responsibleType && r.responsibleId
                    ? { type: r.responsibleType, id: r.responsibleId }
                    : r.executerId
                      ? { type: 'COMPANY', id: r.executerId }
                      : { type: null, id: null };

            const name = (r.responsibleName ?? r.executer ?? '').trim() || nameOfRef(ref);
            m.set(r.id, { ref, name });
        }
        return m;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rows, orgs, companies]);

    // ✅ Draft 로드 (서버가 wbsListId로 필터해서 주는 형태)
    const draftQueryKey = useMemo(() => ['responsibilityDraft', projectId] as const, [projectId]);

    const { data: existingDraft = [], isFetching: draftFetching } = useQuery({
        queryKey: draftQueryKey,
        enabled: !!projectId,
        queryFn: async () => {
            const r = await api.get(`/projects/${projectId}/wbs/responsibilities/draft`);
            // return (r.data?.data ?? []) as Array<{
            //     nodeId: string;
            //     projectCompanyId: string | null;
            //     updatedAt?: string;
            // }>;
            return (r.data?.data ?? []) as Array<{
                nodeId: string;
                projectCompanyId: string | null;
                projectOrgId?: string | null;
                updatedAt?: string;
            }>;
        },
    });

    // server draft -> rowId 기준 edit
    const serverEditsByRowId = useMemo(() => {
        const out: Record<string, RowEdit> = {};
        for (const d of existingDraft) {
            const rowId = rowIdByNodeId.get(d.nodeId);
            if (!rowId) continue;
            // out[rowId] = { rowId, companyId: d.projectCompanyId ?? null };
            const ref: ResponsibleRef = d.projectCompanyId
                ? { type: 'COMPANY', id: d.projectCompanyId }
                : d.projectOrgId
                  ? { type: 'ORG', id: d.projectOrgId }
                  : { type: null, id: null };

            out[rowId] = { rowId, responsibleType: ref.type, responsibleId: ref.id };
        }
        return out;
    }, [existingDraft, rowIdByNodeId]);

    // ✅ merged: server + local overlay
    const mergedEditsByRowId = useMemo(() => {
        const out: Record<string, RowEdit> = { ...serverEditsByRowId };
        for (const [k, v] of Object.entries(local)) out[k] = v;
        return out;
    }, [serverEditsByRowId, local]);

    // function currentCompanyId(rowId: string): string | null {
    //     const e = mergedEditsByRowId[rowId];
    //     if (e) return e.companyId ?? null;
    //     return originalByRowId.get(rowId)?.companyId ?? null;
    // }
    function currentRef(rowId: string): ResponsibleRef {
        const e = mergedEditsByRowId[rowId];
        if (e) return { type: e.responsibleType ?? null, id: e.responsibleId ?? null };

        const orig = originalByRowId.get(rowId)?.ref ?? { type: null, id: null };
        return orig;
    }

    // ✅ 핵심 규칙: 선택한 WBS에 회사 지정하면 자기+모든 자식에 동일 적용
    // function setCompanySubtree(rowId: string, nextCompanyId: string | null) {
    //     setLocal((prev) => {
    //         const next = { ...prev };

    //         // 본인
    //         next[rowId] = { rowId, companyId: nextCompanyId };

    //         // 자식들(있으면 전부)
    //         const descendants = collectDescendants(rowId);
    //         for (const d of descendants) {
    //             next[d.id] = { rowId: d.id, companyId: nextCompanyId };
    //         }

    //         return next;
    //     });
    // }
    function setResponsibleSubtree(rowId: string, next: ResponsibleRef) {
        setLocal((prev) => {
            const out = { ...prev };

            out[rowId] = { rowId, responsibleType: next.type, responsibleId: next.id };

            const descendants = collectDescendants(rowId);
            for (const d of descendants) {
                out[d.id] = { rowId: d.id, responsibleType: next.type, responsibleId: next.id };
            }

            return out;
        });
    }

    // function changeLabel(rowId: string) {
    //     const orig = originalByRowId.get(rowId)?.companyId ?? null;
    //     const origName = originalByRowId.get(rowId)?.companyName || companyNameOf(orig);

    //     const cur = currentCompanyId(rowId);
    //     const curName = companyNameOf(cur);

    //     if (orig === cur) return '—';
    //     if (orig === null && cur === null) return '—';
    //     if (orig === null && cur !== null) return `[신규지정] ${curName}`;
    //     return `[변경] ${origName} → ${curName}`;
    // }
    function changeLabel(rowId: string) {
        const origRef = originalByRowId.get(rowId)?.ref ?? { type: null, id: null };
        const origName = originalByRowId.get(rowId)?.name || nameOfRef(origRef) || '(미지정)';

        const curRef = currentRef(rowId);
        const curName = nameOfRef(curRef) || '(미지정)';

        if (sameRef(origRef, curRef)) return '—';
        return `${origName} → ${curName}`;
    }

    const pendingLocalCount = useMemo(() => Object.keys(local).length, [local]);

    const changedRows = useMemo(() => {
        const out: { rowId: string; code: string; title: string; label: string }[] = [];
        for (const r of rows) {
            const label = changeLabel(r.id);
            if (label === '—') continue;
            out.push({
                rowId: r.id,
                code: (r.code ?? '-') as string,
                title: (r.name ?? '-') as string,
                label,
            });
        }
        return out.sort((a, b) => a.code.localeCompare(b.code));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rows, mergedEditsByRowId, originalByRowId, companies]);

    // function toDraftItems(editsByRowId: Record<string, RowEdit>) {
    //     const items = Object.values(editsByRowId)
    //         .map((e) => {
    //             const row = rowById.get(e.rowId);
    //             if (!row) return null;
    //             return {
    //                 nodeId: row.nodeId,
    //                 projectCompanyId: e.companyId ?? null,
    //             };
    //         })
    //         .filter(Boolean);

    //     return items as Array<{ nodeId: string; projectCompanyId: string | null }>;
    // }
    function toDraftItems(edits: Record<string, RowEdit>) {
        const items = Object.values(edits)
            .map((e) => {
                const row = rowById.get(e.rowId);
                if (!row?.nodeId) return null;

                return {
                    nodeId: row.nodeId,
                    responsibleType: e.responsibleType ?? null,
                    responsibleId: e.responsibleId ?? null,
                };
            })
            .filter(Boolean);

        return items as Array<{
            nodeId: string;
            responsibleType: 'COMPANY' | 'ORG' | null;
            responsibleId: string | null;
        }>;
    }

    async function saveDraft() {
        setErr(null);
        const items = toDraftItems(local);
        if (!items.length) return;

        setBusy(true);
        try {
            await api.post(`/projects/${projectId}/wbs/responsibilities/draft`, { items });

            // ✅ 저장 직후 원복처럼 보이는 현상 방지: draft 즉시 반영
            await qc.invalidateQueries({ queryKey: draftQueryKey });
            await qc.refetchQueries({ queryKey: draftQueryKey });

            setLocal({});
        } catch (e: any) {
            setErr(e?.response?.data?.error?.message ?? e?.message ?? 'Draft save failed.');
        } finally {
            setBusy(false);
        }
    }

    function onClickSave() {
        setConfirmOpen(true);
    }

    async function confirmAndPublish() {
        setErr(null);
        setBusy(true);
        try {
            // local이 있으면 먼저 draft로 저장
            const localItems = toDraftItems(local);
            if (localItems.length) {
                await api.post(`/projects/${projectId}/wbs/responsibilities/draft`, { items: localItems });
            }

            await api.post(`/projects/${projectId}/wbs/responsibilities/draft/publish`, {});

            await qc.invalidateQueries({ queryKey: draftQueryKey });
            await qc.invalidateQueries({ queryKey: ['wbsList', projectId] }); // (프로젝트마다 키 다르면 맞춰주세요)
            await qc.invalidateQueries({ queryKey: ['wbsCurrentList', projectId] }); // (있으면)

            setLocal({});
            setConfirmOpen(false);
            onPublished();
        } catch (e: any) {
            setErr(e?.response?.data?.error?.message ?? e?.message ?? 'Publish failed.');
        } finally {
            setBusy(false);
        }
    }

    function renderTree(list: Row[], depth = 0): React.ReactNode[] {
        const out: React.ReactNode[] = [];
        for (const r of list) {
            out.push(renderRow(r, depth));
            if (!collapsed[r.id] && hasChildren(r.id)) {
                out.push(...renderTree(childrenMap.get(r.id) ?? [], depth + 1));
            }
        }
        return out;
    }

    function renderRow(r: Row, depth: number) {
        const indentPx = depth * 18;
        // const companyId = currentCompanyId(r.id);
        const label = changeLabel(r.id);

        const ref = currentRef(r.id);
        const selectedValue = encodeRef(ref);

        const caret = hasChildren(r.id) ? (
            <button
                type="button"
                className="text-xs w-5 h-5 rounded hover:bg-gray-50 flex items-center justify-center shrink-0"
                onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    toggle(r.id);
                }}
                disabled={busy}
                title={collapsed[r.id] ? '펼치기' : '접기'}
            >
                {collapsed[r.id] ? '▶' : '▼'}
            </button>
        ) : (
            <div className="w-5 shrink-0" />
        );

        return (
            <tr key={r.id} className="border-t">
                {/* 계층코드 */}
                <td className="px-3 py-2 text-xs text-gray-600 w-[120px]">{r.code ?? '-'}</td>

                {/* 항목이름 */}
                <td className="px-3 py-2">
                    <div className="flex items-center gap-2 min-w-0">
                        <div style={{ width: indentPx }} />
                        {caret}
                        <div className="min-w-0 truncate">{r.name ?? '-'}</div>
                    </div>
                </td>

                {/* 담당회사(조직) */}
                <td className="px-3 py-2 w-[300px]">
                    <select
                        className="w-full text-sm border rounded-md px-2 py-1"
                        value={selectedValue}
                        onChange={(e) => setResponsibleSubtree(r.id, decodeRef(e.target.value))}
                        disabled={busy}
                    >
                        <option value="">(미지정)</option>

                        <optgroup label="외부 회사">
                            {(companies as any[]).map((pc) => (
                                <option key={`COMPANY:${pc.id}`} value={`COMPANY:${pc.id}`}>
                                    {pc.company?.name ?? '(unknown)'}
                                </option>
                            ))}
                        </optgroup>

                        <optgroup label="내부 조직">
                            {(orgs as any[]).map((o) => (
                                <option key={`ORG:${o.id}`} value={`ORG:${o.id}`}>
                                    {o.name ?? '(org)'}
                                </option>
                            ))}
                        </optgroup>
                    </select>
                    {/* {hasChildren(r.id) && (
                        <div className="mt-1 text-[11px] text-gray-500">※ 선택 시 하위 항목도 동일하게 적용됩니다.</div>
                    )} */}
                </td>

                {/* 변경 내역 */}
                <td className="px-3 py-2 w-[360px] text-xs">
                    <span className={label === '—' ? 'text-gray-400' : 'text-gray-900'}>{ellipsis(label, 120)}</span>
                </td>
            </tr>
        );
    }

    const draftCount = existingDraft.length;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div className="absolute inset-0 bg-black/30" onClick={() => !busy && onClose()} />

            <div className="relative w-[1220px] max-w-[96vw] max-h-[85vh] bg-white rounded-xl shadow-xl overflow-hidden">
                {/* header */}
                <div className="px-4 py-3 border-b flex items-start gap-3">
                    <div>
                        <div className="text-sm font-semibold">Assign Responsible Company</div>
                        <div className="text-xs text-gray-500">담당회사 배정 (임시 저장: DB Draft)</div>

                        <div className="mt-1 flex items-center gap-2">
                            {draftFetching ? (
                                <span className="text-[11px] px-2 py-1 rounded bg-gray-50 border text-gray-600">
                                    Draft 조회 중…
                                </span>
                            ) : draftCount > 0 ? (
                                <span className="text-[11px] px-2 py-1 rounded bg-emerald-50 border border-emerald-200 text-emerald-800">
                                    Draft 적용 중: {draftCount}건
                                </span>
                            ) : (
                                <span className="text-[11px] px-2 py-1 rounded bg-gray-50 border text-gray-600">
                                    Draft 없음
                                </span>
                            )}

                            {pendingLocalCount > 0 && (
                                <span className="text-[11px] px-2 py-1 rounded bg-amber-50 border border-amber-200 text-amber-900">
                                    로컬 변경 {pendingLocalCount}건
                                </span>
                            )}
                        </div>
                    </div>

                    <div className="ml-auto flex items-center gap-3">
                        <div className='flex gap-2 mr-12'>
                            <button
                                type="button"
                                className="text-xs rounded border px-2.5 py-1.5 hover:bg-gray-50 disabled:opacity-50"
                                disabled={busy}
                                onClick={expandAll}
                            >
                                모두 펼치기
                            </button>
                            <button
                                type="button"
                                className="text-xs rounded border px-2.5 py-1.5 hover:bg-gray-50 disabled:opacity-50"
                                disabled={busy}
                                onClick={collapseAll}
                            >
                                모두 접기
                            </button>
                        </div>

                        <button
                            type="button"
                            className="text-xs rounded border px-2.5 py-1.5 hover:bg-gray-50 disabled:opacity-50"
                            disabled={busy}
                            onClick={onClose}
                        >
                            닫기
                        </button>
                        <button
                            type="button"
                            className="text-xs rounded border px-2.5 py-1.5 hover:bg-gray-50 disabled:opacity-50"
                            disabled={busy || pendingLocalCount === 0}
                            onClick={saveDraft}
                            title="DB에 임시 저장(Draft)"
                        >
                            임시 저장
                        </button>
                        <button
                            type="button"
                            className="text-xs rounded bg-gray-900 text-white px-2.5 py-1.5 hover:bg-gray-800 disabled:opacity-50"
                            disabled={busy}
                            onClick={onClickSave}
                            title="요약 확인 후 저장(Publish)"
                        >
                            저장
                        </button>
                    </div>
                </div>

                {err && (
                    <div className="m-3 rounded border border-rose-300 bg-rose-50 text-rose-800 text-sm px-3 py-2">
                        {err}
                    </div>
                )}

                {/* body */}
                <div className="p-3 overflow-auto max-h-[calc(85vh-120px)]">
                    <table className="min-w-full text-sm">
                        <thead>
                            <tr className="text-left text-gray-500">
                                <th className="px-3 py-2 w-[120px]">계층코드</th>
                                <th className="px-3 py-2">항목이름</th>
                                <th className="px-3 py-2 w-[300px]">담당회사(조직)</th>
                                <th className="px-3 py-2 w-[360px]">변경 내역</th>
                            </tr>
                        </thead>
                        <tbody>{renderTree(roots, 0)}</tbody>
                    </table>
                </div>

                {/* 요약 모달 (저장 시 1회 confirm) */}
                {confirmOpen && (
                    <div className="absolute inset-0 z-10 flex items-center justify-center">
                        <div className="absolute inset-0 bg-black/30" onClick={() => !busy && setConfirmOpen(false)} />

                        <div className="relative w-[900px] max-w-[94vw] max-h-[80vh] bg-white rounded-xl shadow-xl overflow-hidden">
                            <div className="px-4 py-3 border-b flex items-center">
                                <div className="text-sm font-semibold">변경 사항 요약</div>
                                <div className="ml-auto flex gap-2">
                                    <button
                                        type="button"
                                        className="text-xs rounded border px-2.5 py-1.5 hover:bg-gray-50 disabled:opacity-50"
                                        disabled={busy}
                                        onClick={() => setConfirmOpen(false)}
                                    >
                                        돌아가기
                                    </button>
                                    <button
                                        type="button"
                                        className="text-xs rounded bg-gray-900 text-white px-2.5 py-1.5 hover:bg-gray-800 disabled:opacity-50"
                                        disabled={busy || changedRows.length === 0}
                                        onClick={confirmAndPublish}
                                    >
                                        확정 저장
                                    </button>
                                </div>
                            </div>

                            <div className="p-4 overflow-auto max-h-[calc(80vh-70px)]">
                                {changedRows.length === 0 ? (
                                    <div className="text-sm text-gray-600">실제 변경 사항이 없습니다.</div>
                                ) : (
                                    <>
                                        <div className="text-xs text-gray-500 mb-2">
                                            아래 변경 사항이 저장(Publish)됩니다. (기준값은 모달 오픈 시 DB에서 내려온
                                            기존 값)
                                        </div>

                                        <table className="min-w-full text-sm">
                                            <thead>
                                                <tr className="text-left text-gray-500">
                                                    <th className="py-2 pr-3 w-[120px]">계층코드</th>
                                                    <th className="py-2 pr-3">항목</th>
                                                    <th className="py-2 pr-3 w-[420px]">변경</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {changedRows.map((c) => (
                                                    <tr key={c.rowId} className="border-t">
                                                        <td className="py-2 pr-3 text-xs text-gray-600">{c.code}</td>
                                                        <td className="py-2 pr-3">{c.title}</td>
                                                        <td className="py-2 pr-3 text-xs">{c.label}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
